import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    IconButton,
    Button,
    FormControl
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { IEntityAccess } from '../../../model/security/request/create-user-request';
import { IAccessProfileResponse } from '../../../model/base/response/access-profile-response';
import { IPersonEntityResponse } from '../../../model/base/response/person-entity-response';

interface EntityAccessListProps {
    entityAccesses: IEntityAccess[];
    personEntities: IPersonEntityResponse[];
    accessProfiles: IAccessProfileResponse[];
    onAdd: () => void;
    onRemove: (index: number) => void;
    onChange: (index: number, field: string, value: any) => void;
}

const EntityAccessList: React.FC<EntityAccessListProps> = ({
    entityAccesses,
    personEntities,
    accessProfiles,
    onAdd,
    onRemove,
    onChange
}) => {
    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Vincule o usuário a entidades/municípios específicos com diferentes perfis de acesso
            </Typography>

            {entityAccesses.length > 0 && (
                <TableContainer
                    component={Paper}
                    sx={{
                        mb: 2,
                        boxShadow: 'none',
                        border: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Entidade *</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Perfil de Acesso *</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', width: 80 }} align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entityAccesses.map((access, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        '&:hover': { bgcolor: 'action.hover' },
                                        '&:last-child td': { borderBottom: 0 }
                                    }}
                                >
                                    <TableCell sx={{ py: 1.5 }}>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={access.personEntityId || ''}
                                                onChange={(e) => onChange(index, 'personEntityId', e.target.value)}
                                                displayEmpty
                                                sx={{
                                                    bgcolor: 'background.paper',
                                                    '& .MuiSelect-select': {
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            maxHeight: 300
                                                        }
                                                    }
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>Selecione uma entidade</em>
                                                </MenuItem>
                                                {personEntities.map((entity) => (
                                                    <MenuItem
                                                        key={entity.id}
                                                        value={entity.id}
                                                        sx={{
                                                            whiteSpace: 'normal',
                                                            wordWrap: 'break-word'
                                                        }}
                                                    >
                                                        {entity.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={access.accessProfileId || ''}
                                                onChange={(e) => onChange(index, 'accessProfileId', e.target.value)}
                                                displayEmpty
                                                sx={{
                                                    bgcolor: 'background.paper',
                                                    '& .MuiSelect-select': {
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            maxHeight: 300
                                                        }
                                                    }
                                                }}
                                            >
                                                <MenuItem value="">
                                                    <em>Selecione um perfil</em>
                                                </MenuItem>
                                                {accessProfiles.map((profile) => (
                                                    <MenuItem
                                                        key={profile.accessProfileId}
                                                        value={profile.accessProfileId}
                                                        sx={{
                                                            whiteSpace: 'normal',
                                                            wordWrap: 'break-word'
                                                        }}
                                                    >
                                                        {profile.description}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 1.5 }}>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => onRemove(index)}
                                            title="Remover vínculo"
                                            sx={{
                                                '&:hover': {
                                                    bgcolor: 'error.lighter',
                                                }
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {entityAccesses.length === 0 && (
                <Box
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        border: '1px dashed',
                        borderColor: 'grey.300',
                        mb: 2
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Nenhuma entidade vinculada. Clique em "Adicionar Entidade" para começar.
                    </Typography>
                </Box>
            )}

            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={onAdd}
                size="medium"
                fullWidth
                sx={{
                    borderStyle: 'dashed',
                    '&:hover': {
                        borderStyle: 'solid',
                    }
                }}
            >
                Adicionar Entidade
            </Button>
        </Box>
    );
};

export default EntityAccessList;
